import usersApi from "@/api/users.api";
import termsApi from "@/api/terms.api";
import { UserData } from "@/data/interfaces/UserDataInterface";
import { useCookies } from "vue3-cookies";
import { COOKIE_DATA, COOKIE_TERMS_ACCEPTED } from "@/utils/constants";
import { RootState } from "../types";
import { TermsInterface } from "@/data/interfaces/TermsInterface";

export interface UserState {
  users: UserData[] | any[];
  isEditing: boolean;
  user: UserData | null;
}

const { cookies } = useCookies();

const UserStore = {
  namespaced: true,
  state: {
    users: [],
    isEditing: false,
    user: null,
  } as UserState,
  mutations: {
    setUsers(state: UserState, users: UserData[]) {
      state.users = users;
    },

    addUser(state: UserState, user: UserData) {
      state.users.unshift(user);
    },

    editUser(state: UserState, user: UserData) {
      const index = state.users.findIndex((u) => u?.user_id === user?.user_id);
      state.users[index] = user;
    },

    deleteUser(state: UserState, user: UserData) {
      const index = state.users.findIndex((u) => u?.user_id === user?.user_id);
      state.users.splice(index, 1);
    },

    isEditingUser(state: UserState, isEditing: boolean) {
      state.isEditing = isEditing;
    },

    setUser(state: UserState, user: UserData) {
      state.user = user;
    },
  },
  actions: {
    async saveUser(
      {
        commit,
      }: {
        commit: (mutation: string, payload: any) => void;
      },
      user: UserData
    ) {
      const response = await usersApi.save(user);
      if (response) {
        commit("addUser", response.user);
      }
    },

    async editUser(
      {
        commit,
        rootState,
      }: {
        commit: (mutation: string, payload: any) => void;
        rootState: RootState["state"];
      },
      user: UserData
    ) {
      const response = await usersApi.edit(user?.user_id, user);
      if (response) {
        if (rootState.user.data.user_id == user?.user_id) {
          rootState.user.data = response.user;
          cookies.set(COOKIE_DATA, JSON.stringify(response.user), "8h");
        }
        commit("editUser", response.user);
      }
    },

    async setUser(
      {
        commit,
      }: {
        commit: (mutation: string, payload: any) => void;
      },
      user: UserData
    ) {
      commit("setUser", user);
    },

    async resetPassword(
      {
        commit,
      }: {
        commit: (mutation: string, payload: any) => void;
      },
      user: UserData
    ) {
      console.log(user);
      return await usersApi.reset(user?.username);
    },

    async resetByCode(
      {
        commit,
      }: {
        commit: (mutation: string, payload: any) => void;
      },
      user: UserData
    ) {
      return await usersApi.checkCode(user?.code as string);
    },

    async resetPasswordLink(
      {
        commit,
      }: {
        commit: (mutation: string, payload: any) => void;
      },
      user: UserData
    ) {
      return await usersApi.resetPassword(user);
    },

    async getUser(
      {
        commit,
      }: {
        commit: (mutation: string, payload: any) => void;
      },
      user_id: UserData["user_id"]
    ) {
      const response = await usersApi.get(user_id);

      if (response) {
        commit("setUser", response);
      }
      return response;
    },

    async setEditingUser({
      commit,
    }: {
      commit: (mutation: string, payload: any) => void;
    }) {
      commit("isEditingUser", true);
    },

    async stopEditingUser({
      commit,
    }: {
      commit: (mutation: string, payload: any) => void;
    }) {
      commit("isEditingUser", false);
    },

    async deleteUser(
      {
        commit,
      }: {
        commit: (mutation: string, payload: any) => void;
      },
      user: UserData
    ) {
      await usersApi.delete(user?.user_id);
      commit("deleteUser", user);
    },

    async getTermsAccepted(
      {
        rootState,
      }: {
        rootState: RootState["state"];
      },
      userId: string
    ) {
      const response = await termsApi.terms(userId);
      if (response) {
        rootState.user.termsAccepted = true;
        cookies.set(COOKIE_TERMS_ACCEPTED, response, "8h");
        return response;
      }
    },

    async setTermsAccepted(
      {
        rootState,
      }: {
        rootState: RootState["state"];
      },
      data: { userId: string; terms: TermsInterface }
    ) {
      const response = await termsApi.acceptTerms(data.userId);
      if (response) {
        rootState.user.termsAccepted = true;
        cookies.set(
          COOKIE_TERMS_ACCEPTED,
          (data.terms ?? response).toString(),
          "8h"
        );
        return response;
      }
    },

    async removeTermsAccepted(
      {
        rootState,
      }: {
        rootState: RootState["state"];
      },
      userId: string
    ) {
      const response = await termsApi.delete(userId);
      if (response) {
        rootState.user.termsAccepted = false;
        return response;
      }
    },
  },
};

export default UserStore;
